import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { invoicesApi, creditNotesApi } from '@/services/invoices'
import { invalidateNotificationQueries } from '@/hooks/useNotifications'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import type {
  InvoiceListQuery,
  CreateInvoiceRequest,
  EditInvoiceRequest,
  CreditNoteListQuery,
  CreateCreditNoteRequest,
  EditCreditNoteRequest,
  ListQuery,
} from '@/types'

export const INVOICE_DETAIL_TAB_STALE_TIME = 30_000
export const INVOICE_HISTORY_QUERY = { page: 1, limit: 100 } satisfies ListQuery

function invalidateCreditNoteFinancials(qc: QueryClient) {
  // A credit note changes invoice revenue, outstanding balances and every
  // aggregate derived from the source invoice. Root invalidation marks cached
  // list/detail variants stale while only active screens refetch immediately.
  qc.invalidateQueries({ queryKey: ['invoices'] })
  qc.invalidateQueries({ queryKey: ['clients'] })
  qc.invalidateQueries({ queryKey: ['technicians'] })
  qc.invalidateQueries({ queryKey: ['dashboard'] })
  qc.invalidateQueries({ queryKey: ['payments', 'summary'] })
  invalidatePortalMetricQueries(qc)
}

export function useInvoicesList(q: InvoiceListQuery) {
  return useQuery({
    queryKey: ['invoices', 'list', q],
    queryFn: () => invoicesApi.list(q),
  })
}

// Summary card counts. The KPI cards are a fixed tenant-wide overview and must
// NOT change with the list's search / filter state (Dev bug 2.6.1), so the
// counts are fetched with no filters and a stable query key — typing in search
// or changing filters never refetches them. The backend also ignores filters.
export function useInvoicesSummary() {
  return useQuery({
    queryKey: ['invoices', 'summary'],
    queryFn: () => invoicesApi.summary({}),
  })
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ['invoices', 'detail', id],
    queryFn: () => invoicesApi.get(id as string),
    enabled: !!id,
  })
}

export function invoiceHistoryQueryOptions(
  id: string,
  q: ListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['invoices', 'history', id, q],
    queryFn: () => invoicesApi.history(id, q),
    staleTime,
  })
}

export function useInvoiceHistory(
  id: string | undefined,
  q: ListQuery,
  options?: { staleTime?: number }
) {
  return useQuery({
    ...invoiceHistoryQueryOptions(id as string, q, options?.staleTime),
    enabled: !!id,
  })
}

// Feedback #36: clients with outstanding unpaid invoices (global aggregate).
export function useUnpaidByClient() {
  return useQuery({
    queryKey: ['invoices', 'unpaid-by-client'],
    queryFn: () => invoicesApi.unpaidByClient(),
  })
}

export function useCreateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateInvoiceRequest) => invoicesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
    },
  })
}

export function useUpdateInvoice(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditInvoiceRequest) => invoicesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', id] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useUpdateInvoiceNotes(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (notes: string) => invoicesApi.updateNotes(id, { notes }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', id] }),
  })
}

export function useValidateInvoice(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => invoicesApi.validate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
      qc.invalidateQueries({ queryKey: ['invoices', 'summary'] })
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', id] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
    },
  })
}

// Feedback #39: delete a Pro Forma invoice.
export function useDeleteInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => invoicesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
      qc.invalidateQueries({ queryKey: ['invoices', 'unpaid-by-client'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function creditNotesListQueryOptions(
  q: CreditNoteListQuery,
  staleTime = 0
) {
  return queryOptions({
    queryKey: ['credit-notes', 'list', q],
    queryFn: () => creditNotesApi.list(q),
    staleTime,
  })
}

export function useCreditNotesList(
  q: CreditNoteListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...creditNotesListQueryOptions(q, options?.staleTime),
    enabled: options?.enabled ?? true,
  })
}

export function useCreditNote(id: string | undefined) {
  return useQuery({
    queryKey: ['credit-notes', 'detail', id],
    queryFn: () => creditNotesApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreateCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCreditNoteRequest) => creditNotesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] })
      invalidateCreditNoteFinancials(qc)
    },
  })
}

export function useUpdateCreditNote(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditCreditNoteRequest) =>
      creditNotesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] })
      qc.invalidateQueries({ queryKey: ['credit-notes', 'detail', id] })
      invalidateCreditNoteFinancials(qc)
    },
  })
}

export function useDeleteCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => creditNotesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] })
      invalidateCreditNoteFinancials(qc)
    },
  })
}

export function useSendCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: Parameters<typeof creditNotesApi.send>[1]
    }) => creditNotesApi.send(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] })
      qc.invalidateQueries({ queryKey: ['invoices', 'detail'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}
